import { BLOCKS } from '@contentful/rich-text-types';
import clsx from 'clsx';
import { graphql } from 'gatsby';
import { Section } from '~components/Section';
import { BaseFaq } from '~components/BaseFaq';
import { Image } from '~components/Image';
import { Heading } from '~components/Heading';
import { RichContent } from '~components/RichContent';
import * as sharedStyles from '~styles/shared.module.css';
import * as styles from './Text.module.css';

import type { Options } from '@contentful/rich-text-react-renderer';
import type { ContentBlock } from '~components/types';
import type { ContentfulAsset, ContentfulEntry, ContentfulReference, Theme } from '~models/contentful';

type Props = {
  content: ContentfulComponentSharedText;
};

const getReference = (references: ContentfulReference[], id: string) =>
  references.find((ref) => ref.contentful_id === id);

const getRenderNode = (body: { raw: string; references: ContentfulReference[] }): Options['renderNode'] => ({
  [BLOCKS.HEADING_1]: (_, children) => (
    <Heading level={1} force className={styles.heading1}>
      {children}
    </Heading>
  ),
  [BLOCKS.HEADING_2]: (_, children) => (
    <Heading level={2} force className={styles.heading2}>
      {children}
    </Heading>
  ),
  [BLOCKS.HEADING_3]: (_, children) => (
    <Heading level={3} force className={styles.heading3}>
      {children}
    </Heading>
  ),
  [BLOCKS.HEADING_4]: (_, children) => (
    <Heading level={4} force className={styles.heading4}>
      {children}
    </Heading>
  ),
  [BLOCKS.HEADING_5]: (_, children) => (
    <Heading level={5} force className={styles.heading5}>
      {children}
    </Heading>
  ),
  [BLOCKS.HEADING_6]: (_, children) => (
    <Heading level={6} force className={styles.heading6}>
      {children}
    </Heading>
  ),
  [BLOCKS.PARAGRAPH]: (_, children) => <p>{children}</p>,
  [BLOCKS.EMBEDDED_ENTRY]: (node) => {
    const reference = getReference(body.references, node.data.target.sys.id) as ContentfulEntry | undefined;
    switch (reference?.__typename) {
      case 'ContentfulComponentSharedCollapsibleList':
        return (
          <BaseFaq
            className={styles.faq}
            questions={reference.listItems.map(({ question, answer }) => ({
              question,
              answer: answer.raw,
            }))}
          />
        );
      default:
        return null;
    }
  },
  [BLOCKS.EMBEDDED_ASSET]: (node) => {
    const reference = getReference(body.references, node.data.target.sys.id) as ContentfulAsset | undefined;
    return reference?.file.contentType.startsWith('image') ? (
      <Image className={styles.image} image={reference} />
    ) : null;
  },
});

const Text: ContentBlock<Props> = ({ content, ...sectionProps }) => {
  const { body } = content;
  const rawBody = body.raw.replace(/~/g, String.fromCharCode(173));

  return (
    <Section {...sectionProps}>
      <RichContent
        className={clsx(sharedStyles.contentWrapperMobile, styles.content)}
        content={{ raw: rawBody }}
        renderNode={getRenderNode(body)}
      />
    </Section>
  );
};

export default Text;

export type ContentfulComponentSharedText = {
  __typename: 'ContentfulComponentSharedText';
  id: string;
  body: {
    raw: string;
    references: ContentfulReference[];
  };
  theme?: Theme;
};

export const query = graphql`
  fragment Text on ContentfulComponentSharedText {
    __typename
    contentful_id
    body {
      raw
      references {
        ... on Node {
          ... on ContentfulAsset {
            ...MixedMedia
          }
        }
        ... on Node {
          ... on ContentfulComponentSharedCollapsibleList {
            __typename
            id
            contentful_id
            title
            listItems {
              question
              answer {
                raw
              }
            }
          }
        }
      }
    }
    theme
  }
`;
